import { safeJsonStringify } from '@tunein/web-utils';
import PropTypes from 'prop-types';
import { createContext, useContext, useMemo } from 'react';
import parseQuery from 'src/common/utils/queryString/parse';
import format from 'string-format';
import url from 'url';
import isServer from '../utils/isServer';

const LocationAndLocalizationContext = createContext();
LocationAndLocalizationContext.displayName = 'LocationAndLocalizationProvider';

const emptyObject = {};

const LocationAndLocalizationProvider = ({
  locale = '',
  location = emptyObject,
  localizations = emptyObject,
  children,
}) => {
  // Memoized because most components consume this context, so a new value on every render of
  // the app root would re-render all of them.
  const value = useMemo(() => {
    const locationObj = (location.pathname !== undefined && url.parse(location.pathname)) || null;
    const query = parseQuery(location.search);

    const getLocalizedText = (name, values) => {
      const text = localizations[name];

      if (text) {
        return (values ? format(text, values) : text) || name;
      }

      // Only log localization issues on the server
      if (!isServer()) {
        return name;
      }

      const message = `'${name}' is not defined in translations.`;
      // eslint-disable-next-line no-console
      console.warn(message, typeof name === 'string' ? '' : safeJsonStringify(name, { indent: 2 }));

      return name;
    };

    return {
      locale,
      location: {
        ...locationObj,
        query,
        search: location.search,
        state: location.state,
      },
      getLocalizedText,
      localizations,
    };
  }, [locale, location, localizations]);

  return (
    <LocationAndLocalizationContext.Provider value={value}>
      {children}
    </LocationAndLocalizationContext.Provider>
  );
};

LocationAndLocalizationProvider.propTypes = {
  locale: PropTypes.string,
  location: PropTypes.object,
  localizations: PropTypes.object,
  children: PropTypes.node.isRequired,
};

function useTranslations() {
  return useContext(LocationAndLocalizationContext).getLocalizedText;
}

export default LocationAndLocalizationProvider;
export { LocationAndLocalizationContext, useTranslations };
